// src/auth/welcomeRequest.js
//
// W2-R1: decides when the app asks the worker for the welcome email. Kept free of
// React and of browser globals (everything is injected) so it can be tested in
// plain Node; AuthProvider wires it to requestWelcomeEmail and sessionStorage.
//
// The worker is the authority on eligibility and on "at most once". This only
// avoids repeating the request on every auth event:
//   - one request at a time per account (an in-flight request covers the
//     INITIAL_SESSION event and the initial getSession() arriving together);
//   - once the worker has ACCEPTED a request, not again in this browser session;
//   - a request that was not accepted (no token yet, network error, worker
//     error) leaves nothing behind, so the next auth event asks again;
//   - forget() on sign-out makes the next sign-in a later session, which is when
//     the worker retries a welcome that could not be delivered.

export const WELCOME_KEY_PREFIX = "dobiecore:welcome-requested:";

/**
 * @param {object} deps
 * @param {() => Promise<boolean>} deps.request  resolves true only when the worker accepted
 * @param {() => Storage} deps.storage           returns sessionStorage; may throw
 * @param {(fn: () => void) => void} deps.defer  schedules fn outside the auth callback
 */
export function createWelcomeRequester({ request, storage, defer }) {
  const inFlightOrDone = new Set();

  const read = (key) => {
    try {
      return storage().getItem(key);
    } catch {
      return null; // storage unavailable (private mode): the server deduplicates
    }
  };
  const write = (key) => {
    try {
      storage().setItem(key, "1");
    } catch {
      // storage unavailable: the in-memory guard still covers this page load
    }
  };

  function maybeRequest(session) {
    const userId = session?.user?.id;
    if (!userId || inFlightOrDone.has(userId)) return;
    if (read(WELCOME_KEY_PREFIX + userId)) return;
    inFlightOrDone.add(userId);
    defer(async () => {
      let accepted = false;
      try {
        accepted = (await request()) === true;
      } catch {
        accepted = false;
      }
      if (accepted) write(WELCOME_KEY_PREFIX + userId);
      else inFlightOrDone.delete(userId);
    });
  }

  function forget() {
    inFlightOrDone.clear();
    try {
      const s = storage();
      for (let i = s.length - 1; i >= 0; i--) {
        const key = s.key(i);
        if (key && key.startsWith(WELCOME_KEY_PREFIX)) s.removeItem(key);
      }
    } catch {
      // nothing to clear
    }
  }

  return { maybeRequest, forget };
}
